import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Smartphone, Plus, Trash2, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { AddCardForm } from './AddCardForm';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Utiliser la clé publique Stripe depuis les variables d'environnement
const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY;

if (!STRIPE_PUBLIC_KEY) {
  console.error('❌ VITE_STRIPE_PUBLIC_KEY is not defined in environment variables');
}

const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;

interface PaymentMethod {
  id: string;
  type: 'card' | 'mobile_money';
  provider: string | null;
  last_four: string | null;
  card_brand: string | null;
  phone_number: string | null;
  is_default: boolean;
  created_at: string;
}

const PaymentMethodsManager = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingMethod, setIsAddingMethod] = useState(false);
  const [newMethodType, setNewMethodType] = useState<'card' | 'mobile_money'>('mobile_money');
  const [newProvider, setNewProvider] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const { user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    if (user) {
      fetchPaymentMethods();
    }
  }, [user]);

  const fetchPaymentMethods = async () => {
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPaymentMethods((data || []) as PaymentMethod[]);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast.error(t('payments.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMethod = async () => {
    if (!user) return;

    if (newMethodType === 'card') {
      // Open Stripe card form
      setIsAddingMethod(true);
      return;
    }

    if (newMethodType === 'mobile_money') {
      if (!newProvider || !newPhoneNumber) {
        toast.error(t('payments.fillAllFields'));
        return;
      }

      // Validate phone number
      const cleanPhone = newPhoneNumber.replace(/\s/g, '');
      if (!/^\+?243[0-9]{9}$/.test(cleanPhone) && !/^0[0-9]{9}$/.test(cleanPhone)) {
        toast.error(t('payments.invalidPhone'));
        return;
      }

      try {
        // Check for duplicate phone number
        const duplicate = paymentMethods.find(
          (m) => m.type === 'mobile_money' && m.phone_number === cleanPhone
        );
        if (duplicate) {
          toast.error('Ce numéro est déjà enregistré');
          return;
        }

        const lastFour = cleanPhone.slice(-4);
        const { error } = await supabase.from('payment_methods').insert({
          user_id: user.id,
          type: 'mobile_money',
          provider: newProvider,
          phone_number: cleanPhone,
          last_four: lastFour,
          is_default: paymentMethods.length === 0
        });

        if (error) {
          console.error('Supabase insert error:', error);
          toast.error(`Erreur: ${error.message}`);
          return;
        }

        toast.success(t('payments.methodAdded'));
        setIsAddingMethod(false);
        setNewProvider('');
        setNewPhoneNumber('');
        fetchPaymentMethods();
      } catch (error: any) {
        console.error('Error adding payment method:', error);
        toast.error(error?.message || t('payments.errorAdding'));
      }
    }
  };

  const handleSetDefault = async (methodId: string) => {
    try {
      // Remove default from all methods
      await supabase
        .from('payment_methods')
        .update({ is_default: false })
        .eq('user_id', user?.id);

      // Set new default
      const { error } = await supabase
        .from('payment_methods')
        .update({ is_default: true })
        .eq('id', methodId);

      if (error) throw error;

      // Update profile default
      const method = paymentMethods.find(m => m.id === methodId);
      if (method && method.type === 'mobile_money') {
        await supabase
          .from('profiles')
          .update({
            default_mobile_number: method.phone_number,
            default_mobile_provider: method.provider
          })
          .eq('id', user?.id);
      }

      toast.success(t('payments.setAsDefaultSuccess'));
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error(t('payments.errorSetDefault'));
    }
  };

  const handleDelete = async (methodId: string) => {
    try {
      const { error } = await supabase
        .from('payment_methods')
        .delete()
        .eq('id', methodId);

      if (error) throw error;

      toast.success(t('payments.methodDeleted'));
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error(t('payments.errorDeleting'));
    }
  };

  if (loading) {
    return <div className="animate-pulse">{t('messaging.loading')}</div>;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Dialog pour ajouter une méthode - toujours disponible */}
      <Dialog open={isAddingMethod} onOpenChange={setIsAddingMethod}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('payments.addTitle')}</DialogTitle>
            <DialogDescription>
              {newMethodType === 'card' 
                ? t('payments.addCardDescription')
                : t('payments.addMobileMoneyDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('payments.paymentType')}</Label>
              <Select value={newMethodType} onValueChange={(value: 'card' | 'mobile_money') => setNewMethodType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">{t('payments.card')}</SelectItem>
                  <SelectItem value="mobile_money">{t('payments.mobileMoney')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newMethodType === 'card' ? (
              <Elements stripe={stripePromise}>
                <AddCardForm 
                  onSuccess={() => {
                    setIsAddingMethod(false);
                    fetchPaymentMethods();
                  }}
                  onCancel={() => setIsAddingMethod(false)}
                />
              </Elements>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>{t('payments.provider')}</Label>
                  <Select value={newProvider} onValueChange={setNewProvider}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('payments.chooseProvider')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="orange">{t('payments.orangeMoney')}</SelectItem>
                      <SelectItem value="airtel">{t('payments.airtelMoney')}</SelectItem>
                      <SelectItem value="mpesa">{t('payments.mpesa')}</SelectItem>
                      <SelectItem value="africell">{t('payments.africellMoney')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('payments.phoneNumber')}</Label>
                  <Input
                    type="tel"
                    autoComplete="off"
                    inputMode="tel"
                    placeholder="+243 XXX XXX XXX"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                  />
                </div>
                <Button onClick={handleAddMethod} className="w-full">
                  {t('payments.save')}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bouton ajouter quand il y a déjà des méthodes */}
      {paymentMethods.length > 0 && (
        <div className="flex justify-end">
          <Button size="sm" className="flex-shrink-0" onClick={() => setIsAddingMethod(true)}>
            <Plus className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2" />
            <span className="text-xs md:text-sm">{t('payments.add')}</span>
          </Button>
        </div>
      )}

      <div className="grid gap-3 md:gap-4">
        {paymentMethods.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <p className="text-muted-foreground mb-4 text-xs md:text-sm">{t('payments.noMethods')}</p>
              <Button onClick={() => setIsAddingMethod(true)} className="text-xs md:text-sm h-8 md:h-10 px-3 md:px-4">
                <Plus className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-2" />
                <span className="md:hidden">Ajouter</span>
                <span className="hidden md:inline">{t('payments.addFirstMethod')}</span>
              </Button>
            </CardContent>
          </Card>
        ) : (
          paymentMethods.map((method) => (
            <Card key={method.id} className={method.is_default ? 'ring-2 ring-primary' : ''}>
              <CardHeader className="p-3 md:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                    {method.type === 'card' ? (
                      <CreditCard className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                    ) : (
                      <Smartphone className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-sm md:text-base truncate">
                        {method.type === 'card' ? (
                          <>
                            {method.card_brand} ···· {method.last_four}
                          </>
                        ) : (
                          <>
                            {method.provider === 'orange' && 'Orange Money'}
                            {method.provider === 'airtel' && 'Airtel Money'}
                            {method.provider === 'mpesa' && 'M-Pesa'}
                            {method.provider === 'africell' && 'Africell Money'}
                            {' '}···· {method.last_four}
                          </>
                        )}
                      </CardTitle>
                      <CardDescription className="text-xs truncate">
                        {method.type === 'mobile_money' && method.phone_number}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row items-end md:items-center gap-1 md:gap-2 flex-shrink-0">
                    {method.is_default ? (
                      <Badge variant="secondary" className="text-[10px] md:text-xs h-6">
                        <Star className="h-2.5 w-2.5 md:h-3 md:w-3 mr-1 fill-current" />
                        <span className="hidden md:inline">{t('payments.default')}</span>
                        <span className="md:hidden">✓</span>
                      </Badge>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetDefault(method.id)}
                        className="h-6 px-2 text-[10px] md:text-xs"
                      >
                        <span className="hidden md:inline">{t('payments.setAsDefault')}</span>
                        <span className="md:hidden">Par défaut</span>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(method.id)}
                      className="h-6 w-6 p-0"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsManager;
